// ============================================
// MEDFLOW - Dashboard Page
// ============================================

import { useQuery } from '@tanstack/react-query';
import { Calendar, Users, Clock, TrendingUp, Plus, ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { appointmentsApi, api } from '@/lib/api';
import { cn, formatTime, getStatusColor, getStatusLabel } from '@/lib/utils';

interface Appointment {
  id: string;
  startTime: string;
  status: string;
  patient?: { name: string };
  service?: { name: string };
  professional?: { user: { name: string } };
}

interface DashboardStats {
  appointments: {
    todayTotal: number;
    todayCompleted: number;
    todayPending: number;
    todayNoShow: number;
    todayCancelled: number;
    monthTotal: number;
    monthByStatus: Record<string, number>;
  };
  patients: { total: number; newThisMonth: number; blocked: number };
  boxes: { total: number; occupancyToday: number };
  finance: { revenueThisMonth: number; pendingPaymentsCount: number };
}

export function DashboardPage() {
  const today = format(new Date(), 'yyyy-MM-dd');

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await api.get<DashboardStats>('/dashboard/stats');
      return res.data ?? null;
    },
    refetchInterval: () => (document.hidden ? false : 30_000),
    refetchIntervalInBackground: false,
  });

  const { data: todayAppts } = useQuery({
    queryKey: ['dashboard-appointments', today],
    queryFn: async () => {
      const res = await appointmentsApi.list({ startDate: today, endDate: today, limit: 5 });
      return (res?.data as Appointment[]) || [];
    },
  });

  const appointments = todayAppts || [];

  const statCards = [
    {
      label: 'Citas hoy',
      value: stats?.appointments.todayTotal ?? appointments.length,
      icon: Calendar,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      sub: stats ? `${stats.appointments.todayCompleted} completadas` : undefined,
    },
    {
      label: 'Pacientes',
      value: stats?.patients.total ?? '—',
      icon: Users,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      sub: stats ? `${stats.patients.newThisMonth} nuevos este mes` : undefined,
    },
    {
      label: 'Pendientes hoy',
      value: stats?.appointments.todayPending ?? appointments.filter(a => a.status === 'pending' || a.status === 'confirmed').length,
      icon: Clock,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      sub: stats ? `${stats.appointments.monthTotal} citas en el mes` : undefined,
    },
    {
      label: 'Ingresos mes',
      value: stats ? `$${stats.finance.revenueThisMonth.toLocaleString('es-CL')}` : '—',
      icon: TrendingUp,
      iconBg: 'bg-violet-50',
      iconColor: 'text-violet-600',
      sub: stats?.finance.pendingPaymentsCount ? `${stats.finance.pendingPaymentsCount} pagos pendientes` : undefined,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            {format(new Date(), "EEEE d 'de' MMMM", { locale: es }).replace(/^\w/, c => c.toUpperCase())}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Resumen de actividad de la clínica
          </p>
        </div>
        <Link to="/calendar">
          <Button size="sm">
            <Plus className="w-4 h-4 mr-1.5" />
            Nueva cita
          </Button>
        </Link>
      </div>

      {/* Alerts */}
      {stats && (stats.patients.blocked > 0 || stats.appointments.todayNoShow > 0) && (
        <div className="flex flex-wrap gap-3">
          {stats.patients.blocked > 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50/60 px-4 py-2.5 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{stats.patients.blocked} paciente{stats.patients.blocked !== 1 ? 's' : ''} bloqueado{stats.patients.blocked !== 1 ? 's' : ''}</span>
              <Link to="/patients" className="font-medium text-red-800 hover:underline">Ver</Link>
            </div>
          )}
          {stats.appointments.todayNoShow > 0 && (
            <div className="flex items-center gap-2 rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-2.5 text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{stats.appointments.todayNoShow} inasistencia{stats.appointments.todayNoShow !== 1 ? 's' : ''} hoy</span>
            </div>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="rounded-xl border border-gray-100 bg-white p-5 shadow-soft">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 tracking-tight">{stat.value}</p>
                {stat.sub && (
                  <p className="text-xs text-gray-400">{stat.sub}</p>
                )}
              </div>
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', stat.iconBg)}>
                <stat.icon className={cn('w-5 h-5', stat.iconColor)} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Box occupancy */}
      {stats && stats.boxes.total > 0 && (
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-soft">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Ocupación de boxes hoy</h3>
            <span className="text-sm font-semibold text-gray-700">
              {stats.boxes.occupancyToday}%
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
              style={{ width: `${stats.boxes.occupancyToday}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">{stats.boxes.total} box{stats.boxes.total !== 1 ? 'es' : ''} activo{stats.boxes.total !== 1 ? 's' : ''}</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Today's appointments */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-soft">
          <div className="flex items-center justify-between p-5 pb-0">
            <h3 className="text-sm font-semibold text-gray-900">Citas de hoy</h3>
            <Link to="/calendar" className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors">
              Ver todas <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="p-5 pt-4">
            {appointments.length === 0 ? (
              <p className="text-center py-8 text-sm text-gray-400">No hay citas programadas para hoy</p>
            ) : (
              <div className="space-y-2">
                {appointments.slice(0, 5).map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 tabular-nums w-12 flex-shrink-0">
                        {formatTime(apt.startTime)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{apt.patient?.name}</p>
                        <p className="text-xs text-gray-400 truncate">{apt.service?.name}</p>
                      </div>
                    </div>
                    <Badge className={cn(getStatusColor(apt.status), 'ml-2 flex-shrink-0')}>
                      {getStatusLabel(apt.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="rounded-xl border border-gray-100 bg-white shadow-soft">
          <div className="p-5 pb-0">
            <h3 className="text-sm font-semibold text-gray-900">Acciones rápidas</h3>
          </div>
          <div className="p-5 pt-4 space-y-2">
            {[
              { to: '/patients', icon: Users, label: 'Gestionar Pacientes', desc: 'Ver, agregar o editar pacientes' },
              { to: '/calendar', icon: Calendar, label: 'Ver Calendario', desc: 'Gestiona todas las citas' },
              { to: '/finances', icon: TrendingUp, label: 'Finanzas', desc: 'Pagos e ingresos del mes' },
            ].map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="group flex items-center gap-3 p-3.5 rounded-lg border border-gray-100 hover:border-blue-100 hover:bg-blue-50/30 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-50 group-hover:bg-blue-50 flex items-center justify-center transition-colors">
                  <action.icon className="w-4.5 h-4.5 text-gray-500 group-hover:text-blue-600 transition-colors" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{action.label}</p>
                  <p className="text-xs text-gray-400">{action.desc}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 ml-auto flex-shrink-0 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
